import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { NotFoundPageProps } from './NotFoundPage.types';

import { AmbientBloom } from '@/components/ui/AmbientBloom';
import { Button } from '@/components/ui/Button';
import { Wordmark } from '@/components/ui/Logo';

/**
 * NotFoundPage - the catch-all route's page for a URL the app doesn't know.
 * Standalone (no shell), over the void with ambient bloom.
 */
export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  title = 'Page Not Found',
  description = "The page you're looking for doesn't exist or has been moved.",
  showBackButton = true,
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-contrast-950 px-4 py-8">
      {/* Ambient bloom effects */}
      <AmbientBloom
        size="lg"
        strength="mild"
        className="fixed -left-32 -top-32"
      />
      <AmbientBloom
        size="md"
        tone="primary-400"
        className="fixed -bottom-32 -right-32"
      />

      {/* Content card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="relative overflow-hidden rounded-2xl border border-stroke-subtle bg-contrast-700 p-8 text-center backdrop-blur-sm sm:p-10">
          {/* Kanji watermark */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <span
              className="select-none font-display text-[180px] font-bold leading-none"
              style={{
                background:
                  'linear-gradient(180deg, var(--color-sakura-wash) 0%, transparent 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              道
            </span>
          </div>

          <div className="relative mb-6 flex justify-center">
            <Wordmark size={32} />
          </div>

          {/* Title */}
          <h1 className="relative mb-3 font-display text-heading-lg font-bold text-neutral-50">
            {title}
          </h1>

          {/* Description */}
          <p className="relative mb-8 text-body-md text-neutral-400">
            {description}
          </p>

          {/* Back button */}
          {showBackButton && (
            <Button
              variant="ghost"
              size="md"
              onClick={() => navigate(-1)}
              className="relative"
            >
              Go back
            </Button>
          )}
        </div>

        {/* Subtle bottom glow */}
        <div
          className="pointer-events-none absolute -bottom-8 left-1/2 h-16 w-48 -translate-x-1/2 rounded-full opacity-30 blur-2xl"
          style={{ background: 'var(--color-primary-500)' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default NotFoundPage;
